import React from "react";
import { Text, View } from "react-native";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { GlobalStyles } from "../style/Style";
import { HorseData, HorseRaceEvent } from "@freeplay/shared-code";
import { setPlaceColours } from "../picks/HorseLine";
import { HorseLineOutcomeDisplay } from "./HorseLineOutcomeDisplay";
import { CheckmarkIcon } from "../icons/CheckmarkIcon";
import { XIcon } from "../icons/XIcon";

export interface PickEmPropProps {
    propString: string;
    numberOfPlaces: number;
    event: HorseRaceEvent;
    selectedHorses?: number[];
    horsePlacements?: number[];
}

export const PickEmOutcomeDisplay = ({
    propString,
    numberOfPlaces,
    event,
    selectedHorses,
    horsePlacements,
}: PickEmPropProps): JSX.Element => {
    const styles = useDynamicStyles(generateStyles);

    const getIcon = (isWinner: boolean | null) => {
        if (isWinner === null) {
            return <></>;
        }

        const icon = isWinner ? (
            <CheckmarkIcon fill={EStyleSheet.value(GlobalStyles.WINNING_PROP)} />
        ) : (
            <XIcon fill={EStyleSheet.value(GlobalStyles.LOSING_PROP)} />
        );

        return (
            <View style={[styles.iconContainer, isWinner ? styles.winningBorder : styles.losingBorder]}>
                <View style={styles.iconInnerContainer}>{icon}</View>
            </View>
        );
    };
    const displayPlaceCards = () => {
        const elements: JSX.Element[] = [];

        for (let i = 0; i < numberOfPlaces; ++i) {
            let backgroundColour, textColour;
            let saddleNumber = "";
            let selectedStyle = false;
            if (selectedHorses && i < selectedHorses.length) {
                [backgroundColour, textColour] = setPlaceColours(selectedHorses[i]);
                saddleNumber = selectedHorses[i].toString();
                selectedStyle = true;
            } else if (horsePlacements && i < horsePlacements.length) {
                [backgroundColour, textColour] = setPlaceColours(horsePlacements[i]);
                saddleNumber = horsePlacements[i].toString();
            }
            let isWinner = null;
            if (horsePlacements && selectedHorses) {
                isWinner = horsePlacements[i] === selectedHorses[i];
            }
            elements.push(
                <View key={i} style={[styles.placeCard, horsePlacements && styles.hasIcon]}>
                    <View
                        style={[
                            styles.placeCardHeader,
                            horsePlacements && styles.placeCardHeaderWithIcon,
                            selectedStyle ? styles.selected : {},
                            isWinner !== null && isWinner && styles.wonBackground,
                            isWinner !== null && !isWinner && styles.lostBackground,
                        ]}
                    >
                        {horsePlacements && <>{getIcon(isWinner)}</>}
                        <Text style={[styles.placeHeaderText, horsePlacements && styles.placeHeaderTextWithIcon]}>
                            {i + 1}
                            <Text style={styles.ordinalText}>{getOrdinalString(i + 1)}</Text>
                        </Text>
                    </View>
                    <View
                        style={[
                            styles.placeCardContent,
                            selectedStyle ? styles.selectedBorder : {},
                            isWinner !== null && isWinner && styles.winningBorder,
                            isWinner !== null && !isWinner && styles.losingBorder,
                        ]}
                    >
                        <View style={[styles.placeCardContentInner, { backgroundColor: backgroundColour }]}>
                            <Text style={[styles.placeCardContentText, { color: textColour }]}>{saddleNumber}</Text>
                        </View>
                    </View>
                </View>,
            );
        }
        return elements;
    };

    const getPlacement = (programNumber: number) => {
        if (!horsePlacements) {
            return Number.POSITIVE_INFINITY;
        }
        // console.log(programNumber, horsePlacements, horsePlacements.indexOf(programNumber));
        return horsePlacements.indexOf(programNumber);
    };

    const sortHorsesViaFinishingOrder = (a: HorseData, b: HorseData) => {
        if (getPlacement(a.programNumber) === getPlacement(b.programNumber)) {
            return 0;
        } else if (getPlacement(a.programNumber) < 0) {
            return 1;
        } else if (getPlacement(b.programNumber) < 0) {
            return -1;
        } else if (getPlacement(a.programNumber) < getPlacement(b.programNumber)) {
            return -1;
        } else if (getPlacement(b.programNumber) < getPlacement(a.programNumber)) {
            return 1;
        }
        return 0;
    };

    return (
        <View style={[styles.prop]}>
            <Text style={styles.propString}>{propString}</Text>
            <View style={styles.placeContainer}>{displayPlaceCards()}</View>
            <View style={styles.horseContainer}>
                {event.horseData.sort(sortHorsesViaFinishingOrder).map((entry, index) => (
                    <HorseLineOutcomeDisplay
                        key={`horse-${index}`}
                        horseInfo={{
                            horseName: entry.horseName,
                            saddleNumber: entry.programNumber,
                            odds: entry.morningLine,
                        }}
                        index={index}
                        realPlacement={getPlacement(entry.programNumber)}
                    />
                ))}
            </View>
        </View>
    );
};

export const getOrdinalString = (num: number): string => {
    if (num <= 0) return "";

    switch (num % 100) {
        case 11:
        case 12:
        case 13:
            return "th";
    }

    switch (num % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
};

const generateStyles = () => {
    return EStyleSheet.create({
        prop: {
            width: "100%",
            minHeight: "30px",
            justifyContent: "center",
            alignItems: "center",
        },
        propString: {
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
            fontWeight: "700",
            fontSize: 12,
            marginBottom: 2,
        },
        placeContainer: {
            width: "100%",
            display: "flex",
            justifyContent: "center",
            flexDirection: "row",
            flexWrap: "wrap",
        },
        placeCard: {
            width: 48,
            height: 62,
            margin: 2.5,
            marginTop: 0,
        },
        hasIcon: {
            height: 72,
            marginTop: 5,
        },
        placeCardHeader: {
            height: 28,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.HR_PLACE_BACKGROUND),
            borderTopLeftRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
            borderTopRightRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
            justifyContent: "center",
            alignItems: "center",
        },
        wonBackground: {
            backgroundColor: () => EStyleSheet.value(GlobalStyles.WINNING_PROP),
        },
        lostBackground: {
            backgroundColor: () => EStyleSheet.value(GlobalStyles.LOSING_PROP),
        },
        placeCardHeaderWithIcon: {
            height: 38,
        },
        selected: {
            backgroundColor: () => EStyleSheet.value(GlobalStyles.HR_PLACE_BACKGROUND_ACTIVE),
        },
        selectedBorder: {
            borderColor: () => EStyleSheet.value(GlobalStyles.HR_PLACE_BACKGROUND_ACTIVE),
        },
        placeHeaderText: {
            color: () => EStyleSheet.value(GlobalStyles.HR_PLACE_TEXT),
            fontWeight: "700",
            fontSize: 14,
            borderTopRightRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
        },
        placeHeaderTextWithIcon: {
            fontSize: 12,
            lineHeight: 17,
            top: -3,
            position: "relative",
        },
        ordinalText: {
            fontSize: 12,
        },
        placeCardContentText: {
            fontWeight: "700",
        },
        placeCardContent: {
            height: 34,
            borderColor: () => EStyleSheet.value(GlobalStyles.HR_PLACE_BACKGROUND),
            borderBottomLeftRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
            borderBottomRightRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
        },
        placeCardContentInner: {
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderColor: "#ffffff",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            borderBottomLeftRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
            borderBottomRightRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
        },
        horseContainer: {
            width: "100%",
        },
        winningBorder: {
            borderColor: () => EStyleSheet.value(GlobalStyles.WINNING_PROP),
        },
        losingBorder: {
            borderColor: () => EStyleSheet.value(GlobalStyles.LOSING_PROP),
        },
        iconContainer: {
            top: -3,
            position: "relative",
            width: 34,
            height: 26,
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            overflow: "hidden",
        },
        iconInnerContainer: {
            height: 22,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.PROP_BACKGROUND),
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
        },
    });
};
